/* eslint-disable no-nested-ternary */
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import useLayoutStyles from '../../layout/useLayoutStyles';
import UserInfo from '../baseComponents/userInfo';
import useProfilePageStyles from './useProfilePageStyles';
import FollowersFollowingCount from './followersFollowingCount';
import AchievementItem from './achievementItem';
import useGlobalStyles from '../../useGlobalStyles';
import AwardItem from './awardItem';
import { Achievement, AwardedAward } from '../../../types';
import useProfilePage from '../../../hooks/useProfilePage';
import SettingsModal from '../settingsModal';
import { theme } from '../../theme';
import UsersListModal from './usersListModal';
import ActivityItem from './activityItem';
import GameParrot from '../gameParrot';
import {
  setChallenged,
  setChallengeModalOpen,
  setGameResult,
} from '../../../redux/slices/gameParrotSlice';
import { useAppDispatch } from '../../../redux/hooks';

/**
 * The ProfilePage component is a placeholder for the search users page.
 */
const ProfilePage = () => {
  const { classes } = useProfilePageStyles();
  const { classes: layoutClasses } = useLayoutStyles();
  const { classes: globalClasses } = useGlobalStyles();

  const dispatch = useAppDispatch();
  const {
    curUrlUser,
    viewUser,
    currentUser,
    followButtonDisplay,
    handleFollowEvent,
    handleFollowersFollowingClick,
    questionsExpanded,
    setQuestionsExpanded,
    answersExpanded,
    setAnswersExpanded,
    commentsExpanded,
    setCommentsExpanded,
  } = useProfilePage();
  const followLoading =
    followButtonDisplay === 'Following...' || followButtonDisplay === 'Unfollowing...';

  if (!viewUser || curUrlUser !== viewUser?.username) {
    return null;
  }

  /**
   * Renders the ProfilePage component.
   * - userProfileInfo: The user's profile picture and username.
   * - userInfoSettings: The user's settings icon.
   * - followersFollowingCount: The user's follower and following count.
   * - achievements: The container for the user's achievements.
   * - awards: The container for the user's awards which are grouped by name to show the count.
   */
  return (
    viewUser && (
      <>
        <div className={layoutClasses.container}>
          <div className={classes.profileContainer}>
            <div className={classes.userProfileInfo}>
              <div className={classes.userInfoSettings}>
                <UserInfo
                  profilePicture={viewUser.profilePicture}
                  username={viewUser.username || ''}
                  profilePicSize={theme.iconSize}
                />
                {viewUser.username === currentUser?.username ? (
                  <SettingsModal />
                ) : (
                  <>
                    <Button
                      className={`${globalClasses.button} ${classes.followUnfollowButton}
                        ${followLoading && classes.followLoading}`}
                      onClick={() => handleFollowEvent(currentUser?._id || '', viewUser._id || '')}>
                      {followButtonDisplay}
                    </Button>
                    <Tooltip
                      title={`Play ${viewUser.username}`}
                      placement='top'
                      classes={{ tooltip: globalClasses.tooltip }}
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -10],
                            },
                          },
                        ],
                      }}>
                      <img
                        className={`${classes.gameParrotIcon}`}
                        onClick={() => {
                          dispatch(setChallenged(viewUser));
                          dispatch(setGameResult(['incomplete']));
                          dispatch(setChallengeModalOpen(true));
                        }}
                        src={`${process.env.PUBLIC_URL}/assets/branding/gameparrot.png`}
                      />
                    </Tooltip>
                  </>
                )}
              </div>
              <FollowersFollowingCount
                followers={viewUser.followers}
                following={viewUser.following}
                viewUser={viewUser}
                currentUser={currentUser}
                handleFollowersFollowingClick={handleFollowersFollowingClick}
              />
              <UsersListModal />
            </div>
            <hr className={globalClasses.divider} />
            <div className={classes.userActivity}>
              <div className={classes.userActivitySection}>
                <div
                  className={`${layoutClasses.rightPadding} ${layoutClasses.boldTitle} ${classes.sectionTitle}`}
                  onClick={() => setQuestionsExpanded(!questionsExpanded)}>
                  Questions ({viewUser.questions.length})
                  {questionsExpanded ? (
                    <ExpandLessIcon fontSize='large' sx={{ transform: 'translateY(1px)' }} />
                  ) : (
                    <ExpandMoreIcon fontSize='large' sx={{ transform: 'translateY(1px)' }} />
                  )}
                </div>
                <Collapse in={questionsExpanded} timeout='auto' unmountOnExit>
                  <div className={classes.qAC}>
                    {viewUser.questions.length === 0 ? (
                      <div className={`${globalClasses.notExist} ${classes.emptyQACSection}`}>
                        No questions asked yet.
                      </div>
                    ) : (
                      viewUser.questions
                        .slice()
                        .reverse()
                        .map((question, idx) => (
                          <ActivityItem key={idx} activity={question} type='question' />
                        ))
                    )}
                  </div>
                </Collapse>
              </div>
              <div className={classes.userActivitySection}>
                <div
                  className={`${layoutClasses.rightPadding} ${layoutClasses.boldTitle} ${classes.sectionTitle}`}
                  onClick={() => setAnswersExpanded(!answersExpanded)}>
                  Answers ({viewUser.answers.length})
                  {answersExpanded ? (
                    <ExpandLessIcon fontSize='large' sx={{ transform: 'translateY(1px)' }} />
                  ) : (
                    <ExpandMoreIcon fontSize='large' sx={{ transform: 'translateY(1px)' }} />
                  )}
                </div>
                <Collapse in={answersExpanded} timeout='auto' unmountOnExit>
                  <div className={classes.qAC}>
                    {viewUser.answers.length === 0 ? (
                      <div className={`${globalClasses.notExist} ${classes.emptyQACSection}`}>
                        No questions answered yet.
                      </div>
                    ) : (
                      viewUser.answers
                        .slice()
                        .reverse()
                        .map((answer, idx) => (
                          <ActivityItem key={idx} activity={answer} type='answer' />
                        ))
                    )}
                  </div>
                </Collapse>
              </div>
              <div className={classes.userActivitySection}>
                <div
                  className={`${layoutClasses.rightPadding} ${layoutClasses.boldTitle} ${classes.sectionTitle}`}
                  onClick={() => setCommentsExpanded(!commentsExpanded)}>
                  Comments ({viewUser.comments.length})
                  {commentsExpanded ? (
                    <ExpandLessIcon fontSize='large' sx={{ transform: 'translateY(1px)' }} />
                  ) : (
                    <ExpandMoreIcon fontSize='large' sx={{ transform: 'translateY(1px)' }} />
                  )}
                </div>
                <Collapse in={commentsExpanded} timeout='auto' unmountOnExit>
                  <div className={classes.qAC}>
                    {viewUser.comments.length === 0 ? (
                      <div className={`${globalClasses.notExist} ${classes.emptyQACSection}`}>
                        No comments made yet.
                      </div>
                    ) : (
                      viewUser.comments
                        .slice()
                        .reverse()
                        .map((comment, idx) => (
                          <ActivityItem key={idx} activity={comment} type='comment' />
                        ))
                    )}
                  </div>
                </Collapse>
              </div>
            </div>
            <>
              <div>
                <div className={`${layoutClasses.rightPadding} ${layoutClasses.boldTitle}`}>
                  Awards
                </div>
                <div className={classes.achievementAwardContainer}>
                  {viewUser.awards.length === 0 ? (
                    <div className={`${globalClasses.notExist} ${classes.emptySection}`}>
                      No awards received yet.
                    </div>
                  ) : (
                    viewUser.awards
                      .reduce((acc: AwardedAward[], award) => {
                        const found = acc.find(a => a.award === award);
                        if (found) {
                          found.count += 1;
                        } else {
                          acc.push({ award, count: 1 });
                        }
                        return acc;
                      }, [])
                      .map((awardedAward, idx) => (
                        <AwardItem key={idx} awardedAward={awardedAward} />
                      ))
                  )}
                </div>
              </div>
            </>
            <>
              <div className={`${layoutClasses.rightPadding} ${layoutClasses.boldTitle}`}>
                Achievements
              </div>
              <div className={classes.achievementAwardContainer}>
                {(() => {
                  const filteredAchievements: Achievement[] =
                    viewUser.username === currentUser?.username
                      ? viewUser.achievements
                      : viewUser.achievements.filter(
                          achievement => achievement.goal - achievement.progress <= 0,
                        );

                  return filteredAchievements.length === 0 ? (
                    <div className={`${globalClasses.notExist} ${classes.emptySection}`}>
                      No achievements received yet.
                    </div>
                  ) : (
                    filteredAchievements.map((achievement, idx) => (
                      <AchievementItem key={idx} achievement={achievement} />
                    ))
                  );
                })()}
              </div>
            </>
          </div>
        </div>
        <GameParrot />
      </>
    )
  );
};

export default ProfilePage;
